import { faAirplay, faSquareXmark } from '@fortawesome/pro-solid-svg-icons';

import { useTranslate } from '@growtherapy/i18n';
import { Menu, MenuToggleButton } from '@growtherapy/sprout-ui';

import { ControlPanelButton, ControlPanelButtonTooltip } from '../../components/ControlPanelButton';
import { IconMenuItem } from '../../components/IconMenuItem';
import { MENU_Z_INDEX_CLASSNAME } from '../../components/constants';
import { PopoverChevronIndicator } from '../../components/popover/PopoverChevronIndicator';
import { TakeOverScreenShareModal } from './TakeOverScreenShareModal';

export function ScreenShareMenuItems({
  isSharing,
  onShare,
  onStopShare,
}: {
  isSharing: boolean;
  onShare: () => void;
  onStopShare: () => void;
}) {
  const t = useTranslate();
  return (
    <>
      {isSharing ? (
        <>
          <IconMenuItem onClick={onStopShare} destructive icon={faSquareXmark}>
            {t('telehealth-core:screenShare.stopSharing')}
          </IconMenuItem>
          <IconMenuItem icon={faAirplay} onClick={onShare}>
            {t('telehealth-core:screenShare.shareDifferentScreen')}
          </IconMenuItem>
        </>
      ) : (
        <>
          <IconMenuItem onClick={onShare} icon={faAirplay}>
            {t('telehealth-core:screenShare.shareScreen')}
          </IconMenuItem>
        </>
      )}
    </>
  );
}

export function ScreenShareControl({
  isLoading,
  isSharing,
  isShowingScreenShareConfirmation,
  onConfirmShare,
  onHideScreenShareConfirmation,
  onShare,
  onStopShare,
}: {
  isLoading?: boolean;
  isSharing: boolean;
  isShowingScreenShareConfirmation: boolean;
  onConfirmShare: () => void;
  onHideScreenShareConfirmation: () => void;
  onShare: () => void;
  onStopShare: () => void;
}) {
  const t = useTranslate();
  const ariaLabel = isSharing
    ? t('telehealth-core:screenShare.seeScreenshareOptions')
    : t('telehealth-core:screenShare.shareYourScreen');
  const tooltipText = isSharing
    ? t('telehealth-core:screenShare.youAreSharingScreen')
    : t('telehealth-core:screenShare.shareScreen');

  const shareButton = isSharing ? (
    <MenuToggleButton<typeof ControlPanelButton>
      as={ControlPanelButton}
      aria-label={ariaLabel}
      data-tooltip-place='top'
      iconDefinition={faAirplay}
      loading={isLoading}
      shouldOverrideClassName
      visibleCaret={false}
    >
      <PopoverChevronIndicator />
    </MenuToggleButton>
  ) : (
    <ControlPanelButton aria-label={ariaLabel} loading={isLoading} iconDefinition={faAirplay} onClick={onShare} />
  );

  return (
    <div className='hidden md:block'>
      {/** TODO: CARED-1967 The screen-share confirmation modal should be its own component */}
      <TakeOverScreenShareModal
        isModalOpen={isShowingScreenShareConfirmation}
        onCloseModal={onHideScreenShareConfirmation}
        onConfirmShare={onConfirmShare}
      />
      {isSharing ? (
        <Menu
          menuClassName={MENU_Z_INDEX_CLASSNAME}
          placement='top'
          toggleButton={shareButton}
          toggleButtonTooltip={tooltipText}
        >
          <ScreenShareMenuItems isSharing={isSharing} onShare={onShare} onStopShare={onStopShare} />
        </Menu>
      ) : (
        <ControlPanelButtonTooltip childIsInteractive text={tooltipText}>
          {shareButton}
        </ControlPanelButtonTooltip>
      )}
    </div>
  );
}
